import { assign, setup } from "xstate";
import {
  ChecklistPosition,
  diagnosisIdFor,
  type ChecklistCode,
} from "../../lib/checklist/checklist-position";
import {
  derivePreflightChecklist,
  isReady,
  type PreflightChecklistInputs,
} from "../../lib/checklist/derive-preflight-checklist";
import { isDefaultPin } from "../../lib/pin-crypto";

/**
 * Parsed pairing payload from the primary QR scan or manual connection entry.
 *
 * @since 0.0.0
 */
export interface OnboardingPayload {
  readonly insuiteRestUrl: string;
  readonly solToken: string;
}

/**
 * Onboarding wizard context.
 *
 * @since 0.0.0
 */
export interface OnboardingContext {
  payload: OnboardingPayload | null;
  practiceName: string | undefined;
  /** Who supplied `practiceName`: a typed value is never overwritten by a served one. */
  practiceNameSource: "user" | "server" | undefined;
  deviceName: string;
  cardReaderId: string | undefined;
  pin: string;
  checklist: readonly ChecklistPosition[];
  error: string | undefined;
  /** Set when the Bridge refused or could not answer the preflight checklist request. */
  checklistError: string | undefined;
}

/**
 * Onboarding wizard events.
 *
 * @since 0.0.0
 */
export type OnboardingEvent =
  | { type: "NEXT" }
  | { type: "OPEN_PREPARATION" }
  | { type: "SCAN" }
  | { type: "SCANNED"; payload: OnboardingPayload }
  | { type: "MANUAL_ENTRY"; payload: OnboardingPayload }
  | { type: "SCAN_ERROR"; message?: string }
  | { type: "TOKEN_ENTERED"; solToken: string }
  | { type: "CHECKLIST_FAILED"; message?: string }
  | { type: "EDIT_TOKEN" }
  | { type: "RESCAN" }
  | { type: "CAMERA_DENIED" }
  | { type: "RETRY" }
  | {
      type: "CHECKLIST_UPDATED";
      positions: readonly ChecklistPosition[];
      practiceName?: string;
    }
  | { type: "SET_CARD_READER"; cardReaderId: string }
  | { type: "SET_PRACTICE_NAME"; practiceName: string }
  | { type: "SET_DEVICE_NAME"; deviceName: string }
  | { type: "SET_PIN"; pin: string }
  | { type: "FINISH" }
  | { type: "PERSIST_SUCCEEDED" }
  | { type: "PERSIST_FAILED"; message?: string };

/**
 * True when a PIN is acceptable for finishing onboarding: at least 4 chars and
 * not the forbidden default `0000`.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * isAcceptablePin("1234") // true
 * isAcceptablePin("0000") // false
 * ```
 */
export function isAcceptablePin(pin: string): boolean {
  return pin.trim().length >= 4 && !isDefaultPin(pin);
}

function applyLocalChecklistValue(
  positions: readonly ChecklistPosition[],
  code: ChecklistCode,
  value: string | undefined,
): readonly ChecklistPosition[] {
  const resolved = Boolean(value?.trim());
  return positions.map((position) => {
    if (position.code !== code) return position;
    return new ChecklistPosition({
      code: position.code,
      severity: position.severity,
      state: resolved ? "ok" : "failed",
      label: position.label,
      fix: position.fix,
      ...(resolved
        ? {}
        : { diagnosisId: position.diagnosisId ?? diagnosisIdFor(code) }),
    });
  });
}

function applyLocalChecklistValues(params: {
  readonly positions: readonly ChecklistPosition[];
  readonly practiceName: string | undefined;
  readonly deviceName: string;
}): readonly ChecklistPosition[] {
  return applyLocalChecklistValue(
    applyLocalChecklistValue(
      params.positions,
      "missing_practice_name",
      params.practiceName,
    ),
    "missing_device_name",
    params.deviceName,
  );
}

/**
 * Maps checklist inputs served by the Bridge (`DeviceChecklistInputs`, same
 * shape as {@link PreflightChecklistInputs}) into the derived positions +
 * practice name the wizard needs. Shells call the `getDeviceChecklist` RPC then
 * pass the result here — the one derivation, single-sourced.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const result = buildChecklistResult(await rpc.getDeviceChecklist({ deviceIdHash, terminalId }))
 * ```
 */
export function buildChecklistResult(inputs: PreflightChecklistInputs): {
  positions: readonly ChecklistPosition[];
  practiceName: string | undefined;
} {
  return {
    positions: derivePreflightChecklist(inputs),
    practiceName: inputs.practiceName,
  };
}

/**
 * The on-device onboarding ("pairing ceremony") state machine: Step-0
 * preparation → welcome → scan → (SOLTOKEN entry when the code carries none) →
 * auto-preflight → configure → success. A refused checklist request is surfaced, and the
 * preflight offers changing the token or scanning again. Rejects
 * the default PIN and models scan/camera failures as designed states.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const actor = createActor(onboardingMachine).start()
 * actor.send({ type: "NEXT" }) // preparation → welcome
 * ```
 */
export const onboardingMachine = setup({
  types: {
    context: {} as OnboardingContext,
    events: {} as OnboardingEvent,
  },
  guards: {
    // A setup QR from the Bridge carries no SOLTOKEN (it never leaves the Bridge); ask for it.
    payloadHasToken: ({ event }) =>
      (event.type === "SCANNED" || event.type === "MANUAL_ENTRY") &&
      event.payload.solToken.trim() !== "",
    tokenEntered: ({ event }) =>
      event.type === "TOKEN_ENTERED" && event.solToken.trim() !== "",
    checklistReady: ({ context }) =>
      context.checklist.length > 0 && isReady(context.checklist),
    pinAcceptable: ({ context }) => isAcceptablePin(context.pin),
    readyToFinish: ({ context }) =>
      isAcceptablePin(context.pin) &&
      context.checklist.length > 0 &&
      isReady(context.checklist),
  },
  actions: {
    applyPayload: assign({
      payload: ({ event }) =>
        event.type === "SCANNED" || event.type === "MANUAL_ENTRY"
          ? event.payload
          : null,
      error: undefined,
      checklistError: undefined,
    }),
    applyToken: assign({
      payload: ({ context, event }) =>
        event.type === "TOKEN_ENTERED" && context.payload !== null
          ? { insuiteRestUrl: context.payload.insuiteRestUrl, solToken: event.solToken.trim() }
          : context.payload,
      checklist: [],
      checklistError: undefined,
    }),
    applyChecklistFailure: assign({
      checklistError: ({ event }) =>
        event.type === "CHECKLIST_FAILED"
          ? (event.message ?? "onboarding.preflight.checklist-error")
          : undefined,
    }),
    resetConnection: assign({
      payload: null,
      checklist: [],
      checklistError: undefined,
      error: undefined,
    }),
    applyChecklist: assign(({ context, event }) => {
      if (event.type !== "CHECKLIST_UPDATED") return {};
      // A name the user typed wins over every refetch; a served name only fills an empty field.
      const keepTyped =
        context.practiceNameSource === "user" &&
        Boolean(context.practiceName?.trim());
      const practiceName = keepTyped
        ? context.practiceName
        : (event.practiceName ?? context.practiceName);
      const practiceNameSource = keepTyped
        ? context.practiceNameSource
        : event.practiceName !== undefined
          ? ("server" as const)
          : context.practiceNameSource;
      return {
        checklistError: undefined,
        checklist: applyLocalChecklistValues({
          positions: event.positions,
          practiceName,
          deviceName: context.deviceName,
        }),
        practiceName,
        practiceNameSource,
      };
    }),
    setCardReader: assign({
      cardReaderId: ({ context, event }) =>
        event.type === "SET_CARD_READER"
          ? event.cardReaderId
          : context.cardReaderId,
    }),
    setPracticeName: assign(({ context, event }) => {
      if (event.type !== "SET_PRACTICE_NAME") return {};
      return {
        practiceName: event.practiceName,
        practiceNameSource: "user" as const,
        checklist: applyLocalChecklistValue(
          context.checklist,
          "missing_practice_name",
          event.practiceName,
        ),
      };
    }),
    setDeviceName: assign(({ context, event }) => {
      if (event.type !== "SET_DEVICE_NAME") return {};
      return {
        deviceName: event.deviceName,
        checklist: applyLocalChecklistValue(
          context.checklist,
          "missing_device_name",
          event.deviceName,
        ),
      };
    }),
    setPin: assign({
      pin: ({ context, event }) =>
        event.type === "SET_PIN" ? event.pin : context.pin,
    }),
    setScanError: assign({
      error: ({ event }) =>
        event.type === "SCAN_ERROR"
          ? (event.message ?? "scan_failed")
          : undefined,
    }),
    applyPersistError: assign({
      error: ({ event }) =>
        event.type === "PERSIST_FAILED"
          ? (event.message ?? "settings.save-error")
          : undefined,
    }),
  },
}).createMachine({
  id: "onboarding",
  initial: "preparation",
  context: {
    payload: null,
    practiceName: undefined,
    practiceNameSource: undefined,
    deviceName: "",
    cardReaderId: undefined,
    pin: "",
    checklist: [],
    error: undefined,
    checklistError: undefined,
  },
  states: {
    preparation: {
      on: { NEXT: "welcome" },
    },
    welcome: {
      on: {
        SCAN: "scanning",
        OPEN_PREPARATION: "preparation",
      },
    },
    scanning: {
      on: {
        SCANNED: [
          { target: "preflight", guard: "payloadHasToken", actions: "applyPayload" },
          { target: "tokenEntry", actions: "applyPayload" },
        ],
        MANUAL_ENTRY: [
          { target: "preflight", guard: "payloadHasToken", actions: "applyPayload" },
          { target: "tokenEntry", actions: "applyPayload" },
        ],
        SCAN_ERROR: { target: "scanError", actions: "setScanError" },
        CAMERA_DENIED: "cameraDenied",
      },
    },
    scanError: {
      on: {
        RETRY: "scanning",
        MANUAL_ENTRY: [
          { target: "preflight", guard: "payloadHasToken", actions: "applyPayload" },
          { target: "tokenEntry", actions: "applyPayload" },
        ],
        OPEN_PREPARATION: "preparation",
      },
    },
    cameraDenied: {
      on: {
        RETRY: "scanning",
        MANUAL_ENTRY: [
          { target: "preflight", guard: "payloadHasToken", actions: "applyPayload" },
          { target: "tokenEntry", actions: "applyPayload" },
        ],
      },
    },
    tokenEntry: {
      on: {
        TOKEN_ENTERED: { target: "preflight", guard: "tokenEntered", actions: "applyToken" },
        RESCAN: { target: "scanning", actions: "resetConnection" },
      },
    },
    preflight: {
      on: {
        CHECKLIST_UPDATED: { actions: "applyChecklist" },
        CHECKLIST_FAILED: { actions: "applyChecklistFailure" },
        EDIT_TOKEN: { target: "tokenEntry", actions: assign({ checklist: [], checklistError: undefined }) },
        RESCAN: { target: "scanning", actions: "resetConnection" },
        SET_CARD_READER: { actions: "setCardReader" },
        SET_PRACTICE_NAME: { actions: "setPracticeName" },
        SET_DEVICE_NAME: { actions: "setDeviceName" },
        SET_PIN: { actions: "setPin" },
        FINISH: { target: "persisting", guard: "readyToFinish", actions: assign({ error: undefined }) },
      },
    },
    persisting: {
      on: {
        PERSIST_SUCCEEDED: "success",
        PERSIST_FAILED: { target: "preflight", actions: "applyPersistError" },
      },
    },
    success: { type: "final" },
  },
});
